import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  Box,
  Typography,
  Tooltip,
  Radio,
  Switch,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Fade,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link
} from '@material-ui/core';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@material-ui/icons';
import {
  getHubSpotConfigForClient,
  createHubSpotConfigForClient,
  deleteHubSpotConfigForClient,
  getHubSpotSyncExecutions,
  updateAutoCreateApplicationEnabled,
  updateAutoCreateHubSpotDealsEnabled,
  updateNoteEmailsEnabled,
  updateDestinationEntityType,
  updateSyncDocumentsEnabled,
  updateMaxDocumentSyncSizeMb,
  getSyncedDocuments,
  retrySyncedDocument
} from '../../../actions/hubspot';
import { Toast } from 'components/shared/Toaster';
import HubSpotNoteEmailRecipients from './HubSpotNoteEmailRecipients';
import TableComponent from 'components/shared/TableComponent';
import HubSpotSyncPreviewDialog from './HubSpotSyncPreviewDialog';
import strings from 'strings'

const HubSpotForm = () => {
  const [apiKey, setApiKey] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hubspotConfigId, setHubspotConfigId] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [lastSyncStatus, setLastSyncStatus] = useState(null);
  const [lastSyncDetails, setLastSyncDetails] = useState(null);
  const [autoCreateApplicationEnabled, setAutoCreateApplicationEnabled] = useState(false);
  const [autoCreateHubSpotDealsEnabled, setAutoCreateHubSpotDealsEnabled] = useState(false);
  const [noteEmailsEnabled, setNoteEmailsEnabled] = useState(false);
  const [noteEmailsSaving, setNoteEmailsSaving] = useState(false);
  const [noteEmailSettings, setNoteEmailSettings] = useState(null);
  const [syncDocumentsEnabled, setSyncDocumentsEnabled] = useState(false);
  const [destinationEntityType, setDestinationEntityType] = useState('DEAL');
  const [maxDocumentSyncSizeMb, setMaxDocumentSyncSizeMb] = useState(20);

  const [syncExecutions, setSyncExecutions] = useState([]);
  const [executionsPagination, setExecutionsPagination] = useState({
    currentPage: 0,
    totalPages: 1,
    totalElements: 0,
    pageSize: 10
  });
  const [loadingExecutions, setLoadingExecutions] = useState(false);

  const [syncedDocuments, setSyncedDocuments] = useState([]);
  const [syncedDocsPagination, setSyncedDocsPagination] = useState({
    currentPage: 0,
    hasNextPage: false,
    pageSize: 20
  });
  const [loadingSyncedDocs, setLoadingSyncedDocs] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showSyncPreview, setShowSyncPreview] = useState(false);
  const [executionDetailsDialog, setExecutionDetailsDialog] = useState({ open: false, execution: null });

  const t = strings.HUBSPOT_CONFIG;

  // Function to parse execution details
  const parseExecutionDetails = (details) => {
    if (!details) return null;

    const lines = details.split('\n').filter(line => line.trim() !== '');
    const result = {
      summary: [],
      sections: {}
    };

    let currentSection = null;
    let summaryEnded = false;

    for (const line of lines) {
      if (line.startsWith('---') && line.endsWith('---')) {
        summaryEnded = true;
        const sectionName = line.replace(/---/g, '').trim();
        currentSection = sectionName;
        result.sections[sectionName] = [];
      } else if (currentSection) {
        result.sections[currentSection].push(line);
      } else if (!summaryEnded) {
        result.summary.push(line);
      }
    }

    return result;
  };

  // ExecutionDetailsDialog component
  const ExecutionDetailsDialog = ({ open, execution, onClose }) => {
    const details = execution?.executionDetails || execution?.errorMessage;
    const parsedDetails = parseExecutionDetails(details);

    const getSectionIcon = (sectionName) => {
      const lowerName = sectionName.toLowerCase();
      if (lowerName.includes('matched')) {
        return <CheckCircleIcon style={{ color: 'var(--status-approved)' }} />;
      } else if (lowerName.includes('not found')) {
        return <ErrorIcon style={{ color: 'var(--status-declined)' }} />;
      }
      return <InfoIcon style={{ color: '#2196f3' }} />;
    };

    const getSectionColor = (sectionName) => {
      const lowerName = sectionName.toLowerCase();
      if (lowerName.includes('matched')) return '#e8f5e8';
      if (lowerName.includes('not found')) return 'var(--badge-declined-bg)';
      return 'var(--badge-review-bg)';
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="execution-details-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="execution-details-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            <Typography variant="h6">
              {t.EXECUTION_DETAILS || 'Execution Details'}
              {execution && ` #${execution.id}`}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{ maxHeight: '70vh', padding: 0, overflow: 'hidden' }}>
          {parsedDetails ? (
            <Box p={2} style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              {/* Summary Section */}
              {parsedDetails.summary.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, color: 'var(--rox-lilac-ink)' }}>
                    {t.SYNC_SUMMARY || 'Sync Summary'}
                  </Typography>
                  <Box
                    p={2}
                    style={{
                      backgroundColor: 'var(--bg-canvas)',
                      borderRadius: 8,
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    {parsedDetails.summary.map((line, index) => (
                      <Typography key={index} variant="body2" style={{ marginBottom: 4 }}>
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Detailed Sections */}
              {Object.entries(parsedDetails.sections).map(([sectionName, items]) => (
                <Accordion
                  key={sectionName}
                  defaultExpanded={false}
                  style={{
                    marginBottom: 16,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    style={{
                      backgroundColor: getSectionColor(sectionName),
                      borderRadius: '4px 4px 0 0',
                      minHeight: 48
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {getSectionIcon(sectionName)}
                      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        {sectionName}
                      </Typography>
                      <Chip
                        label={items.length}
                        size="small"
                        color="primary"
                        style={{ marginLeft: 8 }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails style={{ padding: 0, display: 'block' }}>
                    <Box style={{ maxHeight: 250, overflowY: 'auto' }}>
                      <List style={{ width: '100%', padding: 0 }}>
                        {items.map((item, index) => (
                          <React.Fragment key={index}>
                            <ListItem style={{ paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8 }}>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                    {item}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            {index < items.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : (
            <Box p={3} textAlign="center">
              <InfoIcon style={{ fontSize: 48, color: '#bdbdbd', marginBottom: 16 }} />
              <Typography variant="body1" color="textSecondary">
                {t.NO_DETAILS_AVAILABLE || 'No details available'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" variant="contained">
            {t.CLOSE || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderLoadingOverlay = () => (
    <Fade in={isLoading}>
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          borderRadius: 8
        }}
      >
        <CircularProgress size={40} />
      </Box>
    </Fade>
  );

  const formatSyncTime = (time) => {
    if (!time) return null;
    return new Date(time).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: false
    });
  };

  const getStatusChip = (status) => {
    if (!status) return null;
    const statusMap = {
      COMPLETED: {
        color: 'default',
        label: t.COMPLETED_SUCCESSFULLY || 'Completed',
        style: {
          backgroundColor: '#e8f5e8',
          color: 'var(--status-approved)',
          border: '1px solid #c8e6c9'
        }
      },
      IN_PROGRESS: { color: 'secondary', label: t.RUNNING || 'Running...' },
      STARTED: { color: 'secondary', label: t.RUNNING || 'Running...' },
      FAILED: { color: 'secondary', label: t.FAILED || 'Failed' }
    };
    const chipData = statusMap[status] || { color: 'default', label: status };
    return (
      <Chip
        label={chipData.label}
        size="small"
        color={chipData.color}
        style={{
          height: 24,
          fontSize: '0.7rem',
          ...chipData.style
        }}
      />
    );
  };

  const executionColumns = [
    { dataField: 'id', text: t.EXECUTION_ID || 'ID', sort: false },
    {
      dataField: 'startedAt',
      text: t.STARTED_AT || 'Started',
      sort: false,
      formatter: (cell) => (cell ? formatSyncTime(cell) : '—')
    },
    {
      dataField: 'completedAt',
      text: t.COMPLETED_AT || 'Completed',
      sort: false,
      formatter: (cell) => (cell ? formatSyncTime(cell) : '—')
    },
    {
      dataField: 'status',
      text: t.STATUS || 'Status',
      sort: false,
      formatter: (cell) => (cell ? getStatusChip(cell) : '—')
    },
    { dataField: 'dealsProcessed', text: t.DEALS_PROCESSED || 'Deals processed', sort: false },
    { dataField: 'dealsMatched', text: t.DEALS_MATCHED || 'Matched', sort: false },
    { dataField: 'notesSynced', text: t.NOTES_SYNCED || 'Notes synced', sort: false },
    {
      dataField: 'errorMessage',
      text: t.ERROR || 'Error',
      sort: false,
      formatter: (cell) => (cell ? (cell.length > 50 ? `${cell.substring(0, 47)}...` : cell) : '—')
    },
    {
      dataField: 'executionDetails',
      text: t.DETAILS || 'Details',
      sort: false,
      formatter: (cell, row) => (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => setExecutionDetailsDialog({ open: true, execution: row })}
        >
          {t.VIEW_DETAILS || 'View details'}
        </Button>
      )
    }
  ];

  const loadSyncExecutions = useCallback(async (pageNumber = 0, pageSize = 10) => {
    if (!isConnected) return;
    setLoadingExecutions(true);
    try {
      const res = await getHubSpotSyncExecutions(pageSize, pageNumber);
      const { response, headers } = res;
      const totalRecords = parseInt(headers?.['x-pagination-total-records']) || 0;
      const totalPages = parseInt(headers?.['x-pagination-total-pages']) || 1;
      setSyncExecutions(Array.isArray(response) ? response : []);
      setExecutionsPagination({
        currentPage: pageNumber,
        totalPages,
        totalElements: totalRecords,
        pageSize
      });
    } catch {
      setSyncExecutions([]);
    } finally {
      setLoadingExecutions(false);
    }
  }, [isConnected]);

  useEffect(() => {
    const loadConfig = async () => {
      setIsLoading(true);
      try {
        const res = await getHubSpotConfigForClient();
        const data = res?.response;

        if (data) {
          setApiKey(data.apiKeyMasked || '');
          setIsConnected(data.connected || false);
          setHubspotConfigId(data.hubSpotConfigId);

          setLastSyncTime(data.lastSyncTime);
          setLastSyncStatus(data.lastSyncStatus);
          setLastSyncDetails(data.lastSyncDetails);
          setAutoCreateApplicationEnabled(Boolean(data.autoCreateApplicationEnabled));
          setAutoCreateHubSpotDealsEnabled(Boolean(data.autoCreateHubSpotDealsEnabled));
          setSyncDocumentsEnabled(data.syncDocumentsEnabled ?? false);
          setNoteEmailsEnabled(data.noteEmailsEnabled ?? false);
          setNoteEmailSettings(data);
          setDestinationEntityType(data.destinationEntityType || 'DEAL');
          setMaxDocumentSyncSizeMb(data.maxDocumentSyncSizeMb ?? 20);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []);

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getSyncedDocStatusChip = (status) => {
    const statusMap = {
      QUEUED:             { label: t.STATUS_QUEUED || 'Queued',           bg: 'var(--neutral-200)', color: '#616161' },
      PUSHED:             { label: t.STATUS_PUSHED || 'Pushed',           bg: 'var(--badge-approved-bg)', color: 'var(--status-approved)' },
      FAILED:             { label: t.STATUS_FAILED || 'Failed',           bg: '#fff8e1', color: '#f57f17' },
      PERMANENTLY_FAILED: { label: t.STATUS_PERMANENTLY_FAILED || 'Perm. failed', bg: 'var(--badge-declined-bg)', color: 'var(--status-declined)' },
      SKIPPED:            { label: t.STATUS_SKIPPED || 'Skipped',         bg: 'var(--bg-canvas)', color: 'var(--neutral-400)' },
    };
    const s = statusMap[status] || { label: status, bg: 'var(--neutral-200)', color: '#616161' };
    return (
      <Chip
        label={s.label}
        size="small"
        style={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.7rem', height: 22, border: `1px solid ${s.color}33` }}
      />
    );
  };

  const syncedDocColumns = [
    {
      dataField: 'fileName',
      text: t.SYNCED_DOC_FILE_NAME || 'File',
      sort: false,
      formatter: (cell) => cell ? (cell.length > 40 ? `${cell.substring(0, 37)}...` : cell) : '—'
    },
    {
      dataField: 'fileSize',
      text: t.SYNCED_DOC_SIZE || 'Size',
      sort: false,
      formatter: (cell) => formatFileSize(cell)
    },
    {
      dataField: 'externalDestinationType',
      text: t.SYNCED_DOC_DESTINATION || 'Destination',
      sort: false,
      formatter: (cell) => cell ? cell.charAt(0) + cell.slice(1).toLowerCase() : '—'
    },
    {
      dataField: 'status',
      text: t.SYNCED_DOC_STATUS || 'Status',
      sort: false,
      formatter: (cell) => cell ? getSyncedDocStatusChip(cell) : '—'
    },
    {
      dataField: 'failureReason',
      text: t.SYNCED_DOC_FAILURE_REASON || 'Failure reason',
      sort: false,
      formatter: (cell) => cell ? (cell.length > 50 ? `${cell.substring(0, 47)}...` : cell) : '—'
    },
    {
      dataField: 'retryCount',
      text: t.SYNCED_DOC_RETRIES || 'Retries',
      sort: false,
      formatter: (cell) => cell ?? '—'
    },
    {
      dataField: 'createdAt',
      text: t.SYNCED_DOC_CREATED_AT || 'Created',
      sort: false,
      formatter: (cell) => cell ? formatSyncTime(cell) : '—'
    },
    {
      dataField: 'syncedAt',
      text: t.SYNCED_DOC_SYNCED_AT || 'Synced at',
      sort: false,
      formatter: (cell) => cell ? formatSyncTime(cell) : '—'
    },
    {
      dataField: 'id',
      text: t.SYNCED_DOC_ACTIONS || 'Actions',
      sort: false,
      formatter: (cell, row) => {
        const retryable = row.status === 'FAILED' || row.status === 'PERMANENTLY_FAILED';
        if (!retryable) return '—';
        return (
          <Button
            size="small"
            variant="outlined"
            disabled={retryingId === row.id}
            onClick={() => handleRetrySync(row.id)}
          >
            {retryingId === row.id
              ? (t.SYNCED_DOC_RETRYING || 'Retrying...')
              : (t.SYNCED_DOC_RETRY || 'Retry')}
          </Button>
        );
      }
    }
  ];

  const loadSyncedDocuments = useCallback(async (pageNumber = 0, pageSize = 20) => {
    if (!isConnected) return;
    setLoadingSyncedDocs(true);
    try {
      const res = await getSyncedDocuments(pageSize, pageNumber, 'createdAt,desc', 'HUBSPOT');
      const { response, headers } = res;
      const hasNextPage = headers?.['x-pagination-has-next-page'] === 'true';
      setSyncedDocuments(Array.isArray(response) ? response : []);
      setSyncedDocsPagination({ currentPage: pageNumber, hasNextPage, pageSize });
    } catch {
      setSyncedDocuments([]);
    } finally {
      setLoadingSyncedDocs(false);
    }
  }, [isConnected]);

  const handleRetrySync = useCallback(async (syncedDocumentId) => {
    setRetryingId(syncedDocumentId);
    try {
      await retrySyncedDocument(syncedDocumentId);
      // Recarga la página actual para reflejar el nuevo status (PUSHED / FAILED / PERM. FAILED)
      await loadSyncedDocuments(syncedDocsPagination.currentPage, syncedDocsPagination.pageSize);
    } catch {
      // opcional: mostrar un toast de error
    } finally {
      setRetryingId(null);
    }
  }, [loadSyncedDocuments, syncedDocsPagination.currentPage, syncedDocsPagination.pageSize]);

  useEffect(() => {
    if (isConnected) {
      loadSyncExecutions(0, 10);
      loadSyncedDocuments(0, 20);
    } else {
      setSyncExecutions([]);
      setSyncedDocuments([]);
    }
  }, [isConnected, loadSyncExecutions, loadSyncedDocuments]);

  const handleVerify = useCallback(async () => {
    if (!apiKey.trim()) {
      Toast({ type: "warning", message: t.PLEASE_ENTER_VALID_API_KEY || "Please enter a valid API key" });
      return;
    }

    setIsLoading(true);
    try {
      const res = await createHubSpotConfigForClient({
        apiKey,
        autoCreateApplicationEnabled,
        autoCreateHubSpotDealsEnabled
      });
      const data = res?.response;

      if (data?.apiKeyMasked) {
        setApiKey(data.apiKeyMasked);
        setIsConnected(true);
        setHubspotConfigId(data.hubSpotConfigId);

        setLastSyncTime(data.lastSyncTime);
        setLastSyncStatus(data.lastSyncStatus);
        setLastSyncDetails(data.lastSyncDetails);
        setAutoCreateApplicationEnabled(Boolean(data.autoCreateApplicationEnabled));
        setAutoCreateHubSpotDealsEnabled(Boolean(data.autoCreateHubSpotDealsEnabled));

        Toast({ type: "success", message: t.CONNECTED_SUCCESSFULLY || "Connected to HubSpot successfully!" });
      } else {
        Toast({ type: "error", message: t.INVALID_API_KEY || "Invalid HubSpot API key" });
      }
    } catch (error) {
      Toast({ type: "error", message: t.FAILED_TO_CONNECT || "Failed to connect to HubSpot" });
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  }, [apiKey, autoCreateApplicationEnabled, autoCreateHubSpotDealsEnabled, t]);

  const handleConfirmDisconnect = useCallback(async () => {
    setShowDisconnectDialog(false);
    setIsLoading(true);

    try {
      await deleteHubSpotConfigForClient(hubspotConfigId);
      setApiKey('');
      setIsConnected(false);
      setHubspotConfigId(null);
      setLastSyncTime(null);
      setLastSyncStatus(null);
      setLastSyncDetails(null);
      setAutoCreateApplicationEnabled(false);
      setAutoCreateHubSpotDealsEnabled(false);
      setSyncDocumentsEnabled(false);
      setNoteEmailsEnabled(false);
      setNoteEmailSettings(null);
      setDestinationEntityType('DEAL');

      Toast({ type: "success", message: t.DISCONNECTED_SUCCESSFULLY || "Disconnected from HubSpot successfully" });
    } catch (error) {
      Toast({ type: "error", message: t.FAILED_TO_DISCONNECT || "Failed to disconnect from HubSpot" });
    } finally {
      setIsLoading(false);
    }
  }, [hubspotConfigId, t]);

  const handleToggleAutoCreateApplications = useCallback(async (enabled) => {
    setAutoCreateApplicationEnabled(enabled);
    if (!isConnected) return;
    try {
      await updateAutoCreateApplicationEnabled(enabled);
    } catch (error) {
      setAutoCreateApplicationEnabled(!enabled);
      Toast({ type: "error", message: "Failed to update setting" });
    }
  }, [isConnected]);

  const handleToggleAutoCreateHubSpotDeals = useCallback(async (enabled) => {
    setAutoCreateHubSpotDealsEnabled(enabled);
    if (!isConnected) return;
    try {
      await updateAutoCreateHubSpotDealsEnabled(enabled);
    } catch (error) {
      setAutoCreateHubSpotDealsEnabled(!enabled);
      Toast({ type: "error", message: "Failed to update setting" });
    }
  }, [isConnected]);

  const handleToggleNoteEmails = useCallback(async (enabled) => {
    setNoteEmailsEnabled(enabled);
    if (!isConnected) return;
    // The switch stays disabled until this save settles, so saves can't overlap or finish out of order.
    setNoteEmailsSaving(true);
    try {
      await updateNoteEmailsEnabled(enabled);
    } catch {
      setNoteEmailsEnabled(!enabled);
      Toast({ type: "error", message: "Failed to update setting" });
    } finally {
      setNoteEmailsSaving(false);
    }
  }, [isConnected]);

  const handleToggleSyncDocuments = useCallback(async (enabled) => {
    setSyncDocumentsEnabled(enabled);
    if (!isConnected) return;
    try {
      await updateSyncDocumentsEnabled(enabled);
    } catch {
      setSyncDocumentsEnabled(!enabled);
      Toast({ type: "error", message: "Failed to update setting" });
    }
  }, [isConnected]);

  const handleDestinationEntityTypeChange = useCallback(async (value) => {
    const previous = destinationEntityType;
    setDestinationEntityType(value);
    try {
      await updateDestinationEntityType(value);
      Toast({ type: "success", message: t.DESTINATION_UPDATED || "Document sync target updated" });
    } catch {
      setDestinationEntityType(previous);
      Toast({ type: "error", message: t.FAILED_TO_UPDATE_DESTINATION || "Failed to update document sync target" });
    }
  }, [destinationEntityType, t]);

  const handleMaxSizeChange = useCallback(async (value) => {
    const parsed = parseInt(value, 10);
    if (!Number.isInteger(parsed) || parsed <= 0 || parsed > 100) return;   // ignora valores inválidos (1–100)
    const previous = maxDocumentSyncSizeMb;
    setMaxDocumentSyncSizeMb(parsed);
    try {
      await updateMaxDocumentSyncSizeMb(parsed);
      Toast({ type: "success", message: t.MAX_SIZE_UPDATED || "Max sync size updated" });
    } catch {
      setMaxDocumentSyncSizeMb(previous);
      Toast({ type: "error", message: t.FAILED_TO_UPDATE_MAX_SIZE || "Failed to update max sync size" });
    }
  }, [maxDocumentSyncSizeMb, t]);

  const handleExecutionsPagination = useCallback((paginationParam) => {
    const { page, limit } = paginationParam;
    loadSyncExecutions(page, limit);
  }, [loadSyncExecutions]);

  const renderConnectedState = () => (
    <Box
      style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        borderRadius: 16,
        padding: '24px 28px',
        marginBottom: 24,
        border: '1px solid #e9ecef',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: '#28a745',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 16,
              boxShadow: '0 3px 8px rgba(40, 167, 69, 0.25)'
            }}
          >
            <CheckCircleIcon style={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              style={{
                fontWeight: 600,
                color: '#212529',
                marginBottom: 4
              }}
            >
              {t.ACTIVE_INTEGRATION_CONNECTED || "Integration Active"}
            </Typography>
            <Typography
              variant="body2"
              style={{
                color: 'var(--fg-tertiary)',
                fontWeight: 400
              }}
            >
              Ready for data synchronization
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <Button
            variant="outlined"
            size="medium"
            color="primary"
            onClick={() => setShowSyncPreview(true)}
            disabled={isLoading}
            style={{
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: 8,
            }}
          >
            {t.PREVIEW_SYNC || "Preview sync"}
          </Button>
          <Button
            variant="outlined"
            size="medium"
            onClick={() => setShowDisconnectDialog(true)}
            disabled={isLoading}
            style={{
              borderColor: '#dc3545',
              color: '#dc3545',
              backgroundColor: 'transparent',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: 8,
              '&:hover': {
                backgroundColor: 'rgba(220, 53, 69, 0.04)'
              }
            }}
          >
            {t.DISCONNECT || "Disconnect"}
          </Button>
        </Box>
      </Box>

      <Box
        style={{
          backgroundColor: 'var(--bg-canvas)',
          borderRadius: 12,
          padding: '16px 20px',
          border: '1px solid #e9ecef'
        }}
      >
        <Typography
          variant="body2"
          style={{
            color: 'var(--neutral-700)',
            fontWeight: 500,
            marginBottom: 8
          }}
        >
          {t.YOUR_HUBSPOT_ACCOUNT_KEY || "Connected API Key"}
        </Typography>
        <Box
          style={{
            backgroundColor: 'var(--bg-surface)',
            borderRadius: 8,
            padding: '12px 16px',
            border: '1px solid #dee2e6',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            color: 'var(--neutral-700)',
            wordBreak: 'break-all'
          }}
        >
          {apiKey}
        </Box>
        <Typography
          variant="caption"
          style={{
            color: 'var(--fg-tertiary)',
            marginTop: 8,
            display: 'block',
            fontStyle: 'italic'
          }}
        >
          <Link
            href="https://support.roxwrite.com/hc/en-us/articles/38270963971479-HubSpot-Integration"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            color="inherit"
            >
            {t.WHERE_FIND_API_KEY || "Need help finding your API key?"}
          </Link>
        </Typography>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={2}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 12,
          padding: '14px 16px',
          border: '1px solid #dee2e6'
        }}
      >
        <Box>
          <Typography variant="body2" style={{ fontWeight: 600, color: '#212529' }}>
            {t.AUTO_CREATE_APPLICATIONS || "Auto-create applications from HubSpot"}
          </Typography>
          <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>
            {t.AUTO_CREATE_APPLICATIONS_HELP || "If enabled, Rox will create an application when a deal has exactly one contact and no matching application exists."}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={autoCreateApplicationEnabled}
              onChange={(e) => handleToggleAutoCreateApplications(e.target.checked)}
              color="primary"
              disabled={isLoading}
            />
          }
          label={autoCreateApplicationEnabled ? "On" : "Off"}
        />
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={2}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 12,
          padding: '14px 16px',
          border: '1px solid #dee2e6'
        }}
      >
        <Box>
          <Typography variant="body2" style={{ fontWeight: 600, color: '#212529' }}>
            {t.AUTO_CREATE_HUBSPOT_DEALS || "Create HubSpot deals from Rox applications"}
          </Typography>
          <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>
            {t.AUTO_CREATE_HUBSPOT_DEALS_HELP || "If enabled, sync creates a HubSpot deal for Rox applications with no matching deal."}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={autoCreateHubSpotDealsEnabled}
              onChange={(e) => handleToggleAutoCreateHubSpotDeals(e.target.checked)}
              color="primary"
              disabled={isLoading}
            />
          }
          label={autoCreateHubSpotDealsEnabled ? "On" : "Off"}
        />
      </Box>

      <Box
        mt={2}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 12,
          padding: '14px 16px',
          border: '1px solid #dee2e6'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600, color: '#212529' }}>
              {t.HUBSPOT_SYNC_DOCUMENTS_LABEL || "Sync documents to HubSpot"}
            </Typography>
            <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>
              {t.HUBSPOT_SYNC_DOCUMENTS_HELP || "If enabled, documents uploaded in ROX will be automatically attached to the matching HubSpot object."}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={syncDocumentsEnabled}
                onChange={(e) => handleToggleSyncDocuments(e.target.checked)}
                color="primary"
                disabled={isLoading}
              />
            }
            label={syncDocumentsEnabled ? "On" : "Off"}
          />
        </Box>

        {syncDocumentsEnabled && (
          <Box mt={2} pt={2} style={{ borderTop: '1px solid #f0f0f0' }}>
            <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>
              {t.DOCUMENT_SYNC_DESCRIPTION || "Attach ROX documents in HubSpot as:"}
            </Typography>
            <Box display="flex" alignItems="center" mt={1}>
              {['DEAL', 'COMPANY', 'CONTACT'].map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Radio
                      checked={destinationEntityType === type}
                      onChange={() => handleDestinationEntityTypeChange(type)}
                      color="primary"
                      disabled={isLoading}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t[`DESTINATION_${type}`] || type.charAt(0) + type.slice(1).toLowerCase()}
                    </Typography>
                  }
                  style={{ marginRight: 16 }}
                />
              ))}
            </Box>
            <Box mt={2}>
              <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>
                {t.MAX_SYNC_SIZE_DESCRIPTION || "Max file size to sync (MB). Larger files are skipped."}
              </Typography>
              <Box display="flex" alignItems="center" mt={1}>
                <TextField
                  type="number"
                  size="small"
                  variant="outlined"
                  value={maxDocumentSyncSizeMb}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') { setMaxDocumentSyncSizeMb(''); return; }
                    const parsed = parseInt(v, 10);
                    if (Number.isNaN(parsed)) return;
                    setMaxDocumentSyncSizeMb(Math.min(parsed, 100));
                  }}
                  onBlur={(e) => handleMaxSizeChange(e.target.value)}
                  disabled={isLoading}
                  inputProps={{ min: 1, max: 100, step: 1, style: { width: 80 } }}
                />
                <Typography variant="body2" style={{ marginLeft: 8 }}>MB</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        mt={2}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 12,
          padding: '14px 16px',
          border: '1px solid #dee2e6'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600, color: '#212529' }}>
              {t.HUBSPOT_NOTE_EMAILS || "Email when a HubSpot note is added"}
            </Typography>
            <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>
              {t.HUBSPOT_NOTE_EMAILS_HELP || "If enabled, an email goes out after each sync that brings in new notes from the linked deal."}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={noteEmailsEnabled}
                onChange={(e) => handleToggleNoteEmails(e.target.checked)}
                color="primary"
                disabled={isLoading || noteEmailsSaving}
              />
            }
            label={noteEmailsEnabled ? "On" : "Off"}
          />
        </Box>

        {noteEmailsEnabled && (
          <HubSpotNoteEmailRecipients
            key={noteEmailSettings?.hubSpotConfigId || 'none'}
            settings={noteEmailSettings}
            disabled={isLoading}
            onSaved={setNoteEmailSettings}
          />
        )}
      </Box>
    </Box>
  );

  const renderDisconnectedState = () => (
    <Box
      style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        borderRadius: 16,
        padding: '28px 32px',
        marginBottom: 24,
        border: '1px solid #e9ecef',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
      }}
    >
      <Box display="flex" alignItems="center" mb={3}>
        <Box
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: '#ffc107',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 16,
            boxShadow: '0 3px 8px rgba(255, 193, 7, 0.25)'
          }}
        >
          <ErrorIcon style={{ color: 'white', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            style={{
              fontWeight: 600,
              color: '#212529',
              marginBottom: 4
            }}
          >
            Connect to HubSpot
          </Typography>
          <Typography
            variant="body2"
            style={{
              color: 'var(--fg-tertiary)',
              fontWeight: 400
            }}
          >
            Enter your API key to establish connection
          </Typography>
        </Box>
      </Box>

      <Box mb={3}>
        <Typography
          variant="subtitle2"
          style={{
            color: 'var(--neutral-700)',
            fontWeight: 600,
            marginBottom: 12
          }}
        >
          {t.AUTHENTICATION_PRIVATE_APP_ACCESS || "HubSpot Private App Access Token"}
        </Typography>
        <TextField
          fullWidth
          placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          variant="outlined"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          disabled={isLoading}
          error={!apiKey.trim() && apiKey.length > 0}
          style={{
            backgroundColor: 'var(--bg-surface)',
            borderRadius: 12
          }}
          InputProps={{
            style: {
              borderRadius: 12,
              fontSize: '0.95rem',
              fontFamily: 'monospace',
              padding: '0 4px'
            }
          }}
        />
        <Box
          mt={2}
          style={{
            backgroundColor: 'var(--bg-surface)',
            borderRadius: 12,
            padding: '14px 16px',
            border: '1px solid #dee2e6'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Box>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#212529' }}>
                {t.AUTO_CREATE_APPLICATIONS || "Auto-create applications from HubSpot"}
              </Typography>
              <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>
                {t.AUTO_CREATE_APPLICATIONS_HELP || "If enabled, Rox will create an application when a deal has exactly one contact and no matching application exists."}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={autoCreateApplicationEnabled}
                  onChange={(e) => setAutoCreateApplicationEnabled(e.target.checked)}
                  color="primary"
                  disabled={isLoading}
                />
              }
              label={autoCreateApplicationEnabled ? "On" : "Off"}
            />
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap" mt={2}>
            <Box>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#212529' }}>
                {t.AUTO_CREATE_HUBSPOT_DEALS || "Create HubSpot deals from Rox applications"}
              </Typography>
              <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>
                {t.AUTO_CREATE_HUBSPOT_DEALS_HELP || "If enabled, sync creates a HubSpot deal for Rox applications with no matching deal."}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={autoCreateHubSpotDealsEnabled}
                  onChange={(e) => setAutoCreateHubSpotDealsEnabled(e.target.checked)}
                  color="primary"
                  disabled={isLoading}
                />
              }
              label={autoCreateHubSpotDealsEnabled ? "On" : "Off"}
            />
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap" mt={2}>
            <Box>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#212529' }}>
                {t.HUBSPOT_SYNC_DOCUMENTS_LABEL || "Sync documents to HubSpot"}
              </Typography>
              <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>
                {t.HUBSPOT_SYNC_DOCUMENTS_HELP || "If enabled, documents uploaded in ROX will be automatically attached to the matching HubSpot object."}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={syncDocumentsEnabled}
                  onChange={(e) => setSyncDocumentsEnabled(e.target.checked)}
                  color="primary"
                  disabled={isLoading}
                />
              }
              label={syncDocumentsEnabled ? "On" : "Off"}
            />
          </Box>
        </Box>
        <Box
          style={{
            backgroundColor: 'var(--bg-canvas)',
            borderRadius: 8,
            padding: '12px 16px',
            marginTop: 12,
            border: '1px solid #e9ecef'
          }}
        >
          <Typography
            variant="caption"
            style={{
              color: 'var(--fg-tertiary)',
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <InfoIcon style={{ fontSize: 14, color: 'var(--fg-tertiary)' }} />
            <Link
              href="https://support.roxwrite.com/hc/en-us/articles/38270963971479-HubSpot-Integration"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
            >
              {t.WHERE_FIND_API_KEY || "Find your API key in HubSpot Settings → Integrations → Private Apps"}
            </Link>
          </Typography>
        </Box>
      </Box>

      <Box display="flex" justifyContent="center">
        <Tooltip title={t.VERIFY_CONNECT_TOOLTIP || "Verify and establish connection with HubSpot"} arrow>
          <span>
            <Button
              variant="contained"
              size="large"
              onClick={handleVerify}
              disabled={isLoading || !apiKey.trim()}
              style={{
                backgroundColor: isLoading || !apiKey.trim() ? 'var(--bg-sunken)' : '#007bff',
                color: 'white',
                fontWeight: 500,
                fontSize: '1rem',
                padding: '12px 32px',
                borderRadius: 8,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(0, 123, 255, 0.25)',
                minWidth: 180,
                border: 'none'
              }}
              startIcon={isLoading ? <CircularProgress size={18} style={{ color: 'white' }} /> : <CheckCircleIcon />}
            >
              {isLoading ? 'Connecting...' : (t.VERIFY_CONNECT || "Verify & Connect")}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  const renderSyncSection = () => (
    isConnected && (
      <Box
        style={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          borderRadius: 16,
          padding: '24px 28px',
          marginTop: 24,
          border: '1px solid #e9ecef',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        <Box display="flex" alignItems="center" mb={3}>
          <Box
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: 'var(--fg-tertiary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 12,
              boxShadow: '0 3px 8px rgba(108, 117, 125, 0.25)'
            }}
          >
            <InfoIcon style={{ color: 'white', fontSize: 18 }} />
          </Box>
          <Typography
            variant="h6"
            style={{
              fontWeight: 600,
              color: '#212529',
              fontSize: '1.1rem'
            }}
          >
            {t.SYNCHRONIZATION || "Data Synchronization"}
          </Typography>
        </Box>

        <Box
          style={{
            backgroundColor: 'var(--bg-canvas)',
            borderRadius: 8,
            padding: '8px 12px',
            border: '1px solid #e9ecef',
            marginBottom: 16
          }}
        >
          <Typography
            variant="caption"
            style={{
              color: 'var(--fg-tertiary)',
              fontWeight: 400
            }}
          >
            {t.SYNC_MAY_TAKE_MINUTES || "This process may take a few minutes"}
          </Typography>
        </Box>

        {lastSyncTime ? (
          <Box
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderRadius: 12,
              padding: '16px 20px',
              border: '1px solid #dee2e6'
            }}
          >
            <Typography
              variant="subtitle2"
              style={{
                color: 'var(--neutral-700)',
                fontWeight: 600,
                marginBottom: 12
              }}
            >
              Last Synchronization
            </Typography>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
              <Chip
                label={`${formatSyncTime(lastSyncTime)}`}
                style={{
                  backgroundColor: 'var(--bg-sunken)',
                  color: 'var(--neutral-700)',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  height: 28,
                  marginRight: 10
                }}
                icon={<InfoIcon style={{ color: 'var(--neutral-700)', fontSize: 16 }} />}
              />
              {getStatusChip(lastSyncStatus)}
            </Box>
            {lastSyncDetails && (
              <Box
                style={{
                  backgroundColor: 'var(--bg-canvas)',
                  borderRadius: 8,
                  padding: '12px 16px',
                  border: '1px solid #e9ecef',
                  marginTop: 8
                }}
              >
                <Typography
                  variant="body2"
                  style={{
                    color: 'var(--fg-tertiary)',
                    fontStyle: 'italic',
                    wordBreak: 'break-word',
                    fontSize: '0.85rem'
                  }}
                >
                  {lastSyncDetails}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            style={{
              backgroundColor: 'var(--bg-canvas)',
              borderRadius: 12,
              padding: '16px 20px',
              border: '1px dashed #dee2e6',
              textAlign: 'center'
            }}
          >
            <Typography
              variant="body2"
              style={{
                color: 'var(--fg-tertiary)',
                fontStyle: 'italic'
              }}
            >
              {t.LAST_SYNC_NEVER || "No synchronization has been performed yet"}
            </Typography>
          </Box>
        )}
      </Box>
    )
  );

  const renderSyncedDocumentsTable = () => (
    isConnected && (
      <Card className="mt-3" style={{ overflow: 'visible' }}>
        <CardContent className="p-6">
          <Typography variant="subtitle2" gutterBottom>
            {t.SYNCED_DOCUMENTS_TITLE || "Synced documents"}
          </Typography>
          <TableComponent
            data={syncedDocuments}
            keyField="id"
            columns={syncedDocColumns}
            sort={false}
            loading={loadingSyncedDocs}
            isLoading={loadingSyncedDocs}
            showPagination={true}
            totalRecords={syncedDocsPagination.hasNextPage
              ? (syncedDocsPagination.currentPage + 2) * syncedDocsPagination.pageSize
              : (syncedDocsPagination.currentPage + 1) * syncedDocsPagination.pageSize}
            totalPages={syncedDocsPagination.hasNextPage
              ? syncedDocsPagination.currentPage + 2
              : syncedDocsPagination.currentPage + 1}
            currentPage={syncedDocsPagination.currentPage}
            paginationLimit={syncedDocsPagination.pageSize}
            onChangePagination={({ page, limit }) => loadSyncedDocuments(page, limit)}
            useLocalPagination={false}
          />
        </CardContent>
      </Card>
    )
  );

  const renderExecutionsTable = () => (
    isConnected && (
      <Card className="mt-3" style={{ overflow: 'visible' }}>
        <CardContent className="p-6">
          <Typography variant="subtitle2" gutterBottom>
            {t.SYNC_EXECUTION_HISTORY || "Sync execution history"}
          </Typography>
          <TableComponent
            data={syncExecutions}
            keyField="id"
            columns={executionColumns}
            sort={false}
            loading={loadingExecutions}
            isLoading={loadingExecutions}
            showPagination={true}
            totalRecords={executionsPagination.totalElements}
            totalPages={executionsPagination.totalPages}
            currentPage={executionsPagination.currentPage}
            paginationLimit={executionsPagination.pageSize}
            onChangePagination={handleExecutionsPagination}
            useLocalPagination={false}
          />
        </CardContent>
      </Card>
    )
  );

  return (
    <>
      <Card style={{ position: 'relative' }}>
        <CardContent className="p-6">
          <Box
            style={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              borderRadius: 12,
              padding: '24px 28px',
              marginBottom: 32,
              border: '1px solid #e3e7ed',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center">
                <Box
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: isConnected ? 'var(--rox-lilac-ink)' : 'var(--fg-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 16,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                >
                  {isConnected ? (
                    <CheckCircleIcon style={{ color: 'white', fontSize: 24 }} />
                  ) : (
                    <ErrorIcon style={{ color: 'white', fontSize: 24 }} />
                  )}
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    style={{
                      fontWeight: 600,
                      color: '#1a1a1a',
                      marginBottom: 4,
                      fontSize: '1.5rem'
                    }}
                  >
                    {t.HUBSPOT_CONFIGURATION_TITLE || "HubSpot Integration"}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      color: 'var(--fg-tertiary)',
                      fontWeight: 400
                    }}
                  >
                    Configure and manage your HubSpot data synchronization
                  </Typography>
                </Box>
              </Box>

              <Chip
                label={isConnected ? (t.CONNECTED || 'CONNECTED') : (t.DISCONNECTED || 'DISCONNECTED')}
                style={{
                  backgroundColor: isConnected ? '#e8f5e8' : 'var(--badge-declined-bg)',
                  color: isConnected ? 'var(--status-approved)' : 'var(--status-declined)',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 32,
                  minWidth: 120,
                  border: `1px solid ${isConnected ? '#c8e6c9' : '#ffcdd2'}`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                icon={isConnected ?
                  <CheckCircleIcon style={{ color: 'var(--status-approved)', fontSize: 16 }} /> :
                  <ErrorIcon style={{ color: 'var(--status-declined)', fontSize: 16 }} />
                }
              />
            </Box>

            {isConnected && (
              <Box
                style={{
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  borderRadius: 8,
                  padding: '12px 16px',
                  border: '1px solid rgba(25, 118, 210, 0.2)'
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    color: 'var(--rox-lilac-ink)',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <InfoIcon style={{ fontSize: 14 }} />
                  Your HubSpot integration is active and ready for data synchronization
                </Typography>
              </Box>
            )}
          </Box>

          {isConnected ? renderConnectedState() : renderDisconnectedState()}

          {renderSyncSection()}
        </CardContent>

        {renderLoadingOverlay()}
      </Card>

      {renderSyncedDocumentsTable()}

      {renderExecutionsTable()}

      <ExecutionDetailsDialog
        open={executionDetailsDialog.open}
        execution={executionDetailsDialog.execution}
        onClose={() => setExecutionDetailsDialog({ open: false, execution: null })}
      />

      <HubSpotSyncPreviewDialog
        open={showSyncPreview}
        onClose={() => setShowSyncPreview(false)}
      />

      <Dialog
        open={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        aria-labelledby="disconnect-dialog-title"
      >
        <DialogTitle id="disconnect-dialog-title">{t.CONFIRM_DISCONNECT_TITLE || "Confirm Disconnect"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t.CONFIRM_DISCONNECT_MESSAGE || "Are you sure you want to disconnect HubSpot?"}
            <br />
            <strong>{t.THIS_WILL_DELETE_CONFIG || "This will delete the configuration and all related data."}</strong>
            <br />
            {t.ACTION_CANNOT_UNDONE || "This action cannot be undone."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisconnectDialog(false)} color="primary">
            {t.CANCEL || "Cancel"}
          </Button>
          <Button
            onClick={handleConfirmDisconnect}
            color="secondary"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
            style={{
              backgroundColor: isLoading ? 'var(--status-declined)' : 'transparent',
              color: isLoading ? '#fff' : 'var(--status-declined)'
            }}
          >
            {isLoading ? (t.DISCONNECTING || 'Disconnecting...') : (t.DISCONNECT || 'Disconnect')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HubSpotForm;
